import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  Animated,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useTheme } from '../design-system/hooks/useTheme';
import { Shadows, Radius, FontSize, FontWeight } from '../design-system/theme/tokens';

// ── Types ─────────────────────────────────────────────────────────────────────

export type ToastType = 'error' | 'info' | 'success';

export interface ToastOptions {
  type: ToastType;
  message: string;
  /** Auto-dismiss duration in ms. Default: 4000 */
  duration?: number;
}

interface ToastEntry extends ToastOptions {
  id: string;
}

interface ToastContextValue {
  showToast: (opts: ToastOptions) => void;
}

// ── Context ───────────────────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue | null>(null);

// ── Colors / Config ─────────────────────────────────────────────────────────────

const TOAST_ICONS: Record<ToastType, string> = {
  error:   '✕',
  success: '✓',
  info:    'ℹ',
};

// ── Single Toast component ────────────────────────────────────────────────────

function ToastItem({
  entry,
  onDismiss,
}: {
  entry: ToastEntry;
  onDismiss: (id: string) => void;
}) {
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(20)).current;
  const { colors } = useTheme();

  React.useEffect(() => {
    // Fade + slide in
    Animated.parallel([
      Animated.timing(opacity, { toValue: 1, duration: 220, useNativeDriver: true }),
      Animated.timing(translateY, { toValue: 0, duration: 220, useNativeDriver: true }),
    ]).start();

    // Auto-dismiss
    const timer = setTimeout(() => dismiss(), entry.duration ?? 4000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = () => {
    Animated.parallel([
      Animated.timing(opacity, { toValue: 0, duration: 180, useNativeDriver: true }),
      Animated.timing(translateY, { toValue: 20, duration: 180, useNativeDriver: true }),
    ]).start(() => onDismiss(entry.id));
  };

  const borderColor = entry.type === 'error' ? colors.danger
                    : entry.type === 'success' ? colors.success
                    : colors.brand;

  return (
    <Animated.View
      style={[
        styles.toast,
        {
          opacity,
          transform: [{ translateY }],
          backgroundColor: colors.surface,
          borderColor: colors.border,
          ...Shadows.card,
        },
      ]}
      accessibilityRole="alert"
      accessibilityLiveRegion="polite"
    >
      <Text style={[styles.icon, { color: borderColor as string }]}>{TOAST_ICONS[entry.type]}</Text>
      <Text style={[styles.message, { color: colors.text as string }]} numberOfLines={3}>
        {entry.message}
      </Text>
      <TouchableOpacity onPress={dismiss} style={styles.closeBtn} accessibilityLabel="Dismiss">
        <Text style={[styles.closeText, { color: colors.text as string }]}>×</Text>
      </TouchableOpacity>
    </Animated.View>
  );
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const idCounter = useRef(0);

  const showToast = useCallback((opts: ToastOptions) => {
    const id = String(++idCounter.current);
    setToasts((prev) => {
      // Cap at 3 visible toasts — remove oldest if over limit
      const trimmed = prev.length >= 3 ? prev.slice(1) : prev;
      return [...trimmed, { ...opts, id }];
    });
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <View style={styles.container} pointerEvents="box-none">
        {toasts.map((entry) => (
          <ToastItem key={entry.id} entry={entry} onDismiss={dismiss} />
        ))}
      </View>
    </ToastContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return ctx;
}

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: 48,
    left: 16,
    right: 16,
    gap: 8,
    zIndex: 9999,
  },
  toast: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: Radius.lg,
    borderWidth: 1,
    gap: 10,
  },
  icon: {
    fontSize: FontSize.sm,
    fontWeight: FontWeight.bold,
    width: 18,
    textAlign: 'center',
  },
  message: {
    flex: 1,
    fontSize: FontSize.sm,
    lineHeight: 20,
    fontWeight: FontWeight.medium,
  },
  closeBtn: {
    padding: 2,
  },
  closeText: {
    fontSize: 18,
    lineHeight: 18,
  },
});
